import { Popover } from "@headlessui/react";
import type { UseMutationResult } from "@tanstack/react-query";
import { useMemo } from "react";
import {
    type EmissionRecordView,
    type EventFormInstance,
    EventStatusEnum,
    type Form,
} from "../../../../apiClient/generated";

export const FormPicker = ({
    availableForms,
    formsEnabledForEvent,
    addFormToEventMutation,
    emissionRecord,
}: {
    availableForms: Form[];
    formsEnabledForEvent: Form[];
    addFormToEventMutation: UseMutationResult<
        EventFormInstance,
        unknown,
        { formId: number }
    >;
    emissionRecord: EmissionRecordView;
}) => {
    const formsAvailableToAdd = useMemo(() => {
        const enabledFormIds = formsEnabledForEvent.map((f) => f.id);
        return availableForms.filter((f) => !enabledFormIds.includes(f.id));
    }, [availableForms, formsEnabledForEvent]);

    const canAddForms = ![
        EventStatusEnum.Archived,
        EventStatusEnum.Linked,
        EventStatusEnum.Rejected,
        EventStatusEnum.Completed,
    ].includes(emissionRecord?.mainEvent.eventStatus as unknown as any);

    return (
        <div className="flex items-center gap-2">
            {/* Add button + popover */}
            <Popover className="relative">
                {canAddForms && (
                    <Popover.Button
                        type="button"
                        disabled={addFormToEventMutation.isPending}
                        className="rounded-full border border-dashed border-neutral-400 bg-white px-4 py-1 text-xs font-medium text-neutral-500 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        + Add a new form section
                    </Popover.Button>
                )}

                <Popover.Panel className="absolute left-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-lg ring-1 ring-neutral-100 z-10 overflow-hidden">
                    {({ close }) => (
                        <>
                            <div className="px-3 py-2 border-b border-neutral-100">
                                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">
                                    Available forms
                                </p>
                            </div>

                            {formsAvailableToAdd.length === 0 ? (
                                <p className="text-xs text-neutral-400 px-3 py-3 text-center">
                                    All forms already added
                                </p>
                            ) : (
                                <ul className="py-1">
                                    {formsAvailableToAdd.map((form, i) => (
                                        <li
                                            key={form.id}
                                            className={
                                                i > 0
                                                    ? "border-t border-neutral-50"
                                                    : ""
                                            }
                                        >
                                            <button
                                                type="button"
                                                disabled={
                                                    addFormToEventMutation.isPending
                                                }
                                                onClick={() =>
                                                    addFormToEventMutation.mutate(
                                                        { formId: form.id },
                                                        {
                                                            onSuccess: () =>
                                                                close(),
                                                        },
                                                    )
                                                }
                                                className="w-full text-left px-3 py-2 text-xs text-neutral-700 font-medium rounded-none hover:bg-blue-50 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {form.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {addFormToEventMutation.isError && (
                                <div className="border-t border-red-100 bg-red-50 px-3 py-2">
                                    <p className="text-xs text-red-500">
                                        Failed to add form — please try again.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </Popover.Panel>
            </Popover>
        </div>
    );
};
